'use client';

import { RefObject } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { getIntlText } from '@/lib/i18n/i18n.server';
import { cn } from '@/lib/utils';

import { SvgIcon } from '@/components/SvgIcon';
import Button from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';

import { PagedCarouselClassNames } from './types';
import { scrollToPage } from './useActivePage';

import iconChevronLeftSrc from '~/icons/chevron-left.svg';
import iconChevronRightSrc from '~/icons/chevron-right.svg';

type PagedCarouselControlsProps = {
  viewportRef: RefObject<HTMLUListElement | null>;
  pageCount: number;
  activePage: number;
  classNames?: PagedCarouselClassNames;
};

export function PagedCarouselControls({
  viewportRef,
  pageCount,
  activePage,
  classNames,
}: PagedCarouselControlsProps) {
  const { texts } = useTranslation();

  if (pageCount <= 1) return null;

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-4',
        classNames?.controls
      )}
    >
      <IconButton
        onClick={() => scrollToPage(viewportRef, activePage - 1)}
        disabled={activePage === 0}
        color='primary'
        size='l'
        variant='text'
        aria-label='Previous page'
        classNames={{
          root: 'disabled:bg-transparent',
        }}
      >
        <SvgIcon src={iconChevronLeftSrc} aria-hidden='true' />
      </IconButton>
      <ul
        role='tablist'
        aria-label={texts.menu_carousel_pagination.carousel_pagination}
        className='flex items-center gap-4'
      >
        {Array.from({ length: pageCount }).map((_, pageIndex) => (
          <li key={pageIndex}>
            <Button
              noStyles
              role='tab'
              aria-selected={pageIndex === activePage}
              aria-current={pageIndex === activePage ? 'page' : undefined}
              onClick={() => scrollToPage(viewportRef, pageIndex)}
              className={cn(
                'cursor-pointer h-2 rounded-full duration-300',
                'outline-none focus-visible:ring-1 focus-visible:ring-gray-400 focus-visible:ring-offset-2',
                pageIndex === activePage
                  ? 'w-8 bg-brand-500'
                  : 'w-2 bg-gray-300 hover:bg-gray-400'
              )}
            >
              <span className='sr-only'>
                {getIntlText(texts.menu_carousel_pagination.slide_index, {
                  index: String(pageIndex + 1),
                })}
                {pageIndex === activePage &&
                  texts.menu_carousel_pagination.current_item_text}
              </span>
            </Button>
          </li>
        ))}
      </ul>
      <IconButton
        onClick={() => scrollToPage(viewportRef, activePage + 1)}
        disabled={activePage === pageCount - 1}
        color='primary'
        size='l'
        variant='text'
        aria-label='Next page'
        classNames={{
          root: 'disabled:bg-transparent',
        }}
      >
        <SvgIcon src={iconChevronRightSrc} aria-hidden='true' />
      </IconButton>
      <div className='sr-only' aria-live='polite' aria-atomic='true'>
        {getIntlText(texts.menu_carousel_pagination.slide_index_of_total, {
          index: String(activePage + 1),
          total: String(pageCount),
        })}
      </div>
    </div>
  );
}
